import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { InquiryVariantPreviewDto } from '@travel-planner/library-travel-planner-api';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { InquiryVariantsActions } from './inquiry-variants.actions';
import { LoadingState } from '../../../../core/models/common';
import { CurrentInquiryVariantActions } from './current-inquiry-variant.actions';

export interface InquiryVariantsState extends EntityState<InquiryVariantPreviewDto> {
    loadingState: LoadingState;
    scrollPosition: number;
    totalInquiryVariants: number | null;
    loadingEntityIds: (number | string)[];
    inquiryVariantDrawerOpened: boolean;

    [key: string]: any;
}

export const inquiryVariantsAdapter: EntityAdapter<InquiryVariantPreviewDto> = createEntityAdapter<InquiryVariantPreviewDto>();

const initialState: InquiryVariantsState = inquiryVariantsAdapter.getInitialState({
    loadingState: 'Initial',
    scrollPosition: 0,
    totalInquiryVariants: null,
    loadingEntityIds: [],
    inquiryVariantDrawerOpened: false,
});

export const inquiryVariantsFeature = createFeature({
    name: 'inquiryVariants',
    reducer: createReducer(
        initialState,
        on(InquiryVariantsActions.loadInquiryVariants, (state, {}) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(InquiryVariantsActions.loadInquiryVariantsSuccess, (state, { inquiryVariants, total }) => {
            if (total != null) {
                return {
                    ...inquiryVariantsAdapter.setAll(inquiryVariants, state),
                    totalInquiryVariants: total,
                    loadingState: 'Loaded' as const,
                    scrollPosition: 0
                };
            }

            return {
                ...inquiryVariantsAdapter.addMany(inquiryVariants, state),
                loadingState: 'Loaded' as const
            };
        }),
        on(InquiryVariantsActions.scrollInquiryVariantsList, (state, { scrollPosition }) => {
            return {
                ...state,
                scrollPosition: scrollPosition
            };
        }),
        on(InquiryVariantsActions.updateFormControl, (state, { property, value }) => {
            return {
                ...state,
                [property]: value
            };
        }),
        on(InquiryVariantsActions.addInquiryVariant, (state) => {
            const loadingEntityIds = [...state.loadingEntityIds, 'new-inquiry-variant'];
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(InquiryVariantsActions.addInquiryVariantSuccess, (state) => {
            const loadingEntityIds = [...state.loadingEntityIds].filter(id => id !== 'new-inquiry-variant');
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(CurrentInquiryVariantActions.updateOverviewSuccess, (state, { updatedOverview }) => {
            const loadingEntityIds = [...state.loadingEntityIds].filter(id => id !== updatedOverview.id);
            const existing = state.entities[updatedOverview.id];
            if (existing) {
                return {
                    ...inquiryVariantsAdapter.updateOne({
                        id: updatedOverview.id,
                        changes: updatedOverview as any
                    }, state),
                    loadingEntityIds: loadingEntityIds
                };
            }
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(InquiryVariantsActions.deleteInquiryVariantSuccess, (state, { id }) => {
            return inquiryVariantsAdapter.removeOne(id, state);
        }),
        on(InquiryVariantsActions.toggleInquiryVariantDrawer, (state, { opened }) => ({
            ...state,
            inquiryVariantDrawerOpened: opened != null ? opened : !state.inquiryVariantDrawerOpened
        })),
        on(
            InquiryVariantsActions.loadInquiryVariantsFailure,
            InquiryVariantsActions.deleteInquiryVariantFailure,
            (state) => ({
                ...state,
                loadingState: 'Error' as const,
            })
        ),
        on(InquiryVariantsActions.addInquiryVariantFailure, (state) => ({
            ...state,
            loadingEntityIds: state.loadingEntityIds.filter(id => id !== 'new-inquiry-variant'),
            loadingState: 'Error' as const,
        })),
        on(InquiryVariantsActions.duplicateInquiryVariant, InquiryVariantsActions.copyInquiryVariant, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(InquiryVariantsActions.duplicateInquiryVariantSuccess, InquiryVariantsActions.copyInquiryVariantSuccess, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds].filter(eid => eid !== id);
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(
            InquiryVariantsActions.duplicateInquiryVariantFailure,
            InquiryVariantsActions.copyInquiryVariantFailure,
            (state, { id }) => {
                const loadingEntityIds = [...state.loadingEntityIds].filter(eid => eid !== id);
                return { ...state, loadingEntityIds: loadingEntityIds, loadingState: 'Error' as const };
            }
        ),
    ),
});

const {
    name,
    reducer,
    selectInquiryVariantsState,
} = inquiryVariantsFeature;

const selectSafeInquiryVariantsState = createSelector(
    selectInquiryVariantsState,
    (state) => state ?? initialState
);

const {
    selectIds,
    selectEntities,
    selectAll,
    selectTotal,
} = inquiryVariantsAdapter.getSelectors(selectSafeInquiryVariantsState);

export const selectInquiryVariantsLoadingState = createSelector(
    selectSafeInquiryVariantsState,
    (state: InquiryVariantsState) => state.loadingState
);

export const selectInquiryVariantLoadingState = (id: number | string) =>
    createSelector(selectSafeInquiryVariantsState, (state: InquiryVariantsState) => state.loadingEntityIds.includes(id));

export const selectInquiryVariantsListScrollPosition = createSelector(
    selectSafeInquiryVariantsState,
    (state: InquiryVariantsState) => state.scrollPosition
);

export const selectFormValue = (property: string) =>
    createSelector(selectSafeInquiryVariantsState, (state: InquiryVariantsState) => state[property]);

export const selectInquiryVariants = selectAll;

export const selectInquiryVariantsEntities = selectEntities;

export const selectInquiryVariant = (id: string | number) =>
    createSelector(selectSafeInquiryVariantsState, (state) => state.entities[id] || null);

export const selectInquiryVariantsLoadedCount = selectTotal;

export const selectInquiryVariantsTotalInquiryVariants = createSelector(
    selectSafeInquiryVariantsState,
    (state: InquiryVariantsState) => state.totalInquiryVariants
);

export const isInquiryVariantDrawerOpen = createSelector(
    selectSafeInquiryVariantsState,
    (state: InquiryVariantsState) => state.inquiryVariantDrawerOpened
);
